import { Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'motion/react'
import { useState } from 'react'
import InlineFeedback from './InlineFeedback'
import { DataRow, AnimatedChevron } from '@tendbot/ui'
import type { InfoCardData } from '../../../shared/types/chat'
import type { FeedbackType } from '../types'
import { expandCollapse } from '@tendbot/ui'

interface Props {
  info: InfoCardData
  onAction?: (id: string, label: string, linkedNoteId?: string) => void
}

/**
 * Generic structured data card -- the AI can fill this with anything:
 * research results, weather, status reports, person profiles, etc.
 */
export default function InfoCard({ info, onAction }: Props) {
  const { t } = useTranslation('chat')
  const { t: tc } = useTranslation('common')
  const [expandedSections, setExpandedSections] = useState<number[]>(
    // First section expanded by default if only one; all collapsed if many
    info.sections?.length === 1 ? [0] : []
  )

  const toggleSection = (idx: number) => {
    setExpandedSections(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    )
  }

  const accentBorder = info.accentColor
    ? `border-${info.accentColor}`
    : 'border-gold'

  // For sections with no label (flat rows), render inline
  const flatSections = info.sections?.filter(s => !s.label) || []
  const namedSections = info.sections?.filter(s => s.label) || []

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`glass-card rounded-xl overflow-hidden border-l-4 ${accentBorder}`}
    >
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center gap-2 mb-3">
          <Info size={14} className="text-gold" />
          <span className="text-xs font-semibold uppercase tracking-wider text-gold">
            {info.label || t('info.defaultLabel')}
          </span>
        </div>

        {/* Title */}
        <h4 className="text-ivory font-medium text-sm mb-0.5">{info.title}</h4>
        {info.subtitle && (
          <p className="text-pearl/50 text-xs mb-3">{info.subtitle}</p>
        )}

        {/* Flat rows (no section label) */}
        {flatSections.length > 0 && (
          <div className="space-y-1.5 mb-3">
            {flatSections.flatMap(s => s.rows).map((row, i) => (
              <DataRow key={i} label={row.label} value={row.value} valueClassName={row.highlight ? 'text-gold font-semibold' : undefined} />
            ))}
          </div>
        )}

        {/* Named sections -- collapsible */}
        {namedSections.length > 0 && (
          <div className="space-y-2 mb-3">
            {namedSections.map((section, idx) => {
              const isOpen = expandedSections.includes(idx) || namedSections.length === 1
              return (
                <div key={idx}>
                  {namedSections.length > 1 ? (
                    <button
                      onClick={() => toggleSection(idx)}
                      className="flex items-center gap-1 text-xs text-gold/70 hover:text-gold transition-colors w-full"
                    >
                      <AnimatedChevron isOpen={isOpen} size={12} className="text-gold/70" />
                      <span className="font-medium">{section.label}</span>
                      {section.badge && (
                        <span className="ml-auto text-xs text-pearl/50">{section.badge}</span>
                      )}
                    </button>
                  ) : (
                    <span className="text-xs text-gold/70 font-medium">{section.label}</span>
                  )}

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        {...expandCollapse}
                        className="overflow-hidden"
                      >
                        <div className="mt-1.5 space-y-1.5">
                          {section.rows.map((row, ri) => (
                            <div key={ri} className="flex justify-between text-xs p-2 rounded-lg bg-charcoal/50">
                              <span className="text-pearl/60">{row.label}</span>
                              <span className={`text-pearl ${row.highlight ? 'text-gold font-semibold' : ''}`}>
                                {row.value}
                              </span>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            })}
          </div>
        )}

        {/* Tags */}
        {info.tags && info.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {info.tags.map((tag, i) => (
              <span
                key={i}
                className="px-2 py-0.5 rounded-full text-xs bg-gold/10 text-gold/70 border border-gold/15"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Note */}
        {info.note && (
          <div className="p-2.5 rounded-lg bg-gold/5 border border-gold/10 mb-3">
            <p className="text-xs text-pearl/70 italic">"{info.note}"</p>
          </div>
        )}

        {/* Action buttons */}
        {info.actions && info.actions.length > 0 && (
          <div className="flex gap-2">
            {info.actions.map((action, i) => (
              <button
                key={action.id}
                onClick={() => onAction?.(action.id, action.label, action.linkedNoteId)}
                className={`py-2 px-4 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                  i === 0
                    ? 'flex-1 bg-gold text-midnight hover:bg-gold-light'
                    : 'border border-white/10 text-pearl/70 hover:border-gold/30 hover:text-ivory'
                }`}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}

        {/* Feedback */}
        <div className="flex items-center justify-end gap-1 mt-3 pt-2 border-t border-white/5">
          <span className="text-xs text-pearl/50 mr-1">{tc('feedback.wasHelpful')}</span>
          <InlineFeedback
            id={`info-${info.id}`}
            onFeedback={(id: string, fb: FeedbackType) => console.log('Info feedback:', id, fb)}
          />
        </div>
      </div>
    </motion.div>
  )
}
